import type { FC, PropsWithChildren } from "react";
import React from "react";

import cn from "classnames";

import { Gap } from "../../../../../helpers/common/Gap/Gap";
import { getChildrenRecord } from "../../../../../utils";
import { CONTENT_DEFAULT_SIZE } from "../../../Content/Content";
import { TEXT_SIZE_BY_CONTENT_SIZE_MAP } from "../../../Content/constants/sizes";
import { CONTENTS_VIEW_DISPLAY_NAME } from "../../ContentsView";

import type { PropsWithCustomVarsStyles } from "../../../../../types/react";
import type { ContentSize } from "../../../Content/types";

import classes from "./ContentsViewRow.styl";

const CONTENTS_VIEW_ROW_TEST_ID = "contents-view-row-test-id";

export interface IContentViewRow {
    /**
     * Отвечает за значение параметра `size` у всех вложенных компонентов `Content`
     * */
    size?: ContentSize;
    /**
     * Отвечает за приоритет между левым и правым `Content`
     * */
    columnPriority?: "left" | "equal" | "right" | "none";
}

export type ContentViewRow = PropsWithChildren<IContentViewRow> &
    PropsWithCustomVarsStyles;

export const ContentsViewRow: FC<ContentViewRow> = ({
    children,
    size = CONTENT_DEFAULT_SIZE,
    customVarsStyles,
    columnPriority = "equal",
}) => {
    const expandedChildren = getChildrenRecord(
        children,
        CONTENTS_VIEW_DISPLAY_NAME,
        {
            Content: {
                props: {
                    size,
                },
                required: true,
            },
            Unknown: {
                skipElement: true,
            },
        },
        {
            isArrayResult: true,
        },
    );

    const sizeNumber = TEXT_SIZE_BY_CONTENT_SIZE_MAP[size];
    const itemClassName = cn(
        classes["wrapper"],
        classes["wrapper__item"],
        classes[`__size-${sizeNumber}`],
        customVarsStyles,
    );

    return (
        <Gap
            className={itemClassName}
            contentClassName={classes["container"]}
            data-tsid={CONTENTS_VIEW_ROW_TEST_ID}
        >
            {expandedChildren.map((item, index) => (
                <div
                    key={index}
                    className={cn(
                        classes["item"],
                        classes[`__column-priority-${columnPriority}`],
                    )}
                >
                    {item}
                </div>
            ))}
        </Gap>
    );
};

ContentsViewRow.displayName = "ContentsView.Row";

export default ContentsViewRow;
